import React from 'react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/branding/hole-in-one-logo-3d.png"
                alt="Hole In 1 Logo"
                className="w-12 h-12 object-contain"
              />
              <span className="ml-2 text-xl font-bold text-gray-900">Hole In 1</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <a
            href="/"
            className="inline-flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-6"
          >
            ← Back to Home
          </a>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-2">Effective Date: February 28, 2026</p>
          <p className="text-gray-500 mb-8">Last Updated: February 28, 2026</p>

          <div className="prose prose-lg max-w-none text-gray-700">

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="mb-4">
                Welcome to Hole In 1 ("App," "we," "us," or "our"), a mobile application operated by Kia Kaha Labs LLC ("Company"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Services").
              </p>
              <p className="mb-4">
                This Privacy Policy applies to all users of the Hole In 1 app. By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy. If you do not agree with the terms of this Privacy Policy, please do not access or use the Services.
              </p>
              <p>
                We reserve the right to make changes to this Privacy Policy at any time and for any reason. We will alert you about any changes by updating the "Last Updated" date of this Privacy Policy. You are encouraged to periodically review this Privacy Policy to stay informed of updates.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="mb-4">We collect information about you in various ways when you use our Services. The information we collect includes:</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.1 Personal Information</h3>
              <p className="mb-4">We collect personal information that you voluntarily provide to us when you register for an account, use the Services, or contact us. This includes:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Identity Information:</strong> Full name, username, and profile photos</li>
                <li><strong>Contact Information:</strong> Email addresses and phone numbers</li>
                <li><strong>Account Credentials:</strong> Passwords and authentication information</li>
                <li><strong>Profile Information:</strong> Any information you choose to add to your user profile</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.2 Payment Information</h3>
              <p className="mb-4">Payment transactions are processed through third-party payment processors including Apple Inc., Google LLC, and RevenueCat. We do not directly collect or store your payment card information. However, we may receive limited payment-related information from these processors, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Transaction identifiers</li>
                <li>Subscription status</li>
                <li>Purchase history</li>
                <li>Receipt validation data</li>
              </ul>
              <p className="mb-4">For information about how these payment processors handle your payment information, please review:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Apple's Privacy Policy: <a href="https://www.apple.com/legal/privacy/" className="text-green-600 hover:text-green-700">https://www.apple.com/legal/privacy/</a></li>
                <li>Google's Privacy Policy: <a href="https://policies.google.com/privacy" className="text-green-600 hover:text-green-700">https://policies.google.com/privacy</a></li>
                <li>RevenueCat's Privacy Policy: <a href="https://www.revenuecat.com/privacy" className="text-green-600 hover:text-green-700">https://www.revenuecat.com/privacy</a></li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.3 Location Data</h3>
              <p className="mb-4">We collect geolocation and GPS data when you use location-based features of the App, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Precise location coordinates (latitude and longitude)</li>
                <li>Golf course locations and check-ins</li>
                <li>Location-based search and mapping data</li>
                <li>GPS coordinates at the time of video recording, stored alongside your uploaded content</li>
              </ul>
              <p className="mb-4">
                With your permission, the App may also collect location data in the background to detect when you arrive at a golf course or are near a par 3 hole, in order to send you relevant notifications. Background location data is used for proximity detection only and is not stored or uploaded to our servers. Only the location recorded at the time of a video recording is persisted.
              </p>
              <p>
                You can enable or disable location services (including background location) through your device settings at any time. However, disabling location services may limit certain functionality of the App.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.4 Device Information</h3>
              <p className="mb-4">We automatically collect certain information about your mobile device when you use the Services, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Device identifiers (such as device ID)</li>
                <li>Device model and manufacturer</li>
                <li>Operating system and version</li>
                <li>Mobile network information and carrier</li>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Time zone settings</li>
                <li>Device language preferences</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.5 Push Notification Data</h3>
              <p className="mb-4">When you opt in to receive push notifications, we collect:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Push notification tokens</li>
                <li>Notification preferences</li>
                <li>Notification interaction data (opens, dismissals)</li>
              </ul>
              <p>You can disable push notifications at any time through your device settings.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.6 User-Generated Content</h3>
              <p className="mb-4">We collect content that you create, upload, or share through the Services, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Golf videos (audio and video recordings)</li>
                <li>Photos and images</li>
                <li>Comments and direct messages</li>
                <li>Shot results, hole information, and yardage data</li>
                <li>Social interactions and activity feeds</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.7 Usage and Log Data</h3>
              <p className="mb-4">We automatically collect information about your interaction with the Services, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Pages and screens viewed</li>
                <li>Features and functions used</li>
                <li>Actions taken within the App</li>
                <li>Session duration and frequency</li>
                <li>Timestamps of activities</li>
                <li>Search queries</li>
                <li>Links clicked</li>
                <li>Error logs and crash reports</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">2.8 Social Login Information</h3>
              <p className="mb-4">If you choose to authenticate using third-party social login services (Apple Sign-In or Google Sign-In), we may receive:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Profile information provided by the social login service</li>
                <li>Email address associated with your social account</li>
                <li>Name and profile picture</li>
                <li>Authentication tokens</li>
                <li>Any other information you authorize the social login service to share with us</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Collect Information</h2>
              <p className="mb-4">We collect information through the following methods:</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Directly From You</h3>
              <p className="mb-4">We collect information that you provide directly to us when you:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Create an account or update your profile</li>
                <li>Use features of the App</li>
                <li>Upload content (videos, photos, scores)</li>
                <li>Contact customer support</li>
                <li>Respond to surveys or promotions</li>
                <li>Grant permissions on your device</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Automatically</h3>
              <p className="mb-4">We automatically collect certain information when you access and use the Services through:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Mobile device sensors and APIs</li>
                <li>Log files and error tracking systems</li>
                <li>Third-party SDKs integrated into the App</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 From Third-Party Sources</h3>
              <p className="mb-4">We may receive information about you from third-party services, including:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Social login providers (Apple, Google)</li>
                <li>Payment processors (Apple, Google) and payment and subscription management services</li>
                <li>Mapping and location service providers</li>
                <li>Error monitoring services</li>
                <li>Publicly available sources</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">3.4 From Your Device Contacts</h3>
              <p>
                With your permission, we may access your device's contact list to help you find friends who are already using the App. We use phone numbers from your contacts solely as lookup keys to match against existing users and suggest connections. Your contact data is not uploaded, stored on our servers, or shared with any third parties.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect for the following purposes:</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.1 To Provide and Maintain Services</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Create and manage your user account</li>
                <li>Authenticate your identity and secure your account</li>
                <li>Process transactions and manage subscriptions</li>
                <li>Deliver the features and functionality of the App</li>
                <li>Store and display your user-generated content</li>
                <li>Provide location-based services and golf course information</li>
                <li>Send you push notifications and important service updates</li>
                <li>Track and measure sponsor content exposure to fulfill sponsorship obligations and improve sponsor placements</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.2 To Improve and Optimize Services</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Analyze usage patterns and trends</li>
                <li>Monitor and improve App performance</li>
                <li>Develop new features and functionality</li>
                <li>Conduct research and analytics</li>
                <li>Test and troubleshoot technical issues</li>
                <li>Detect and prevent errors and crashes</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.3 To Communicate With You</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Respond to your inquiries and support requests</li>
                <li>Send administrative information and updates</li>
                <li>Provide customer service</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Request feedback and conduct surveys</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.4 To Ensure Security and Prevent Fraud</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Monitor for suspicious activity and potential security threats</li>
                <li>Enforce our Terms of Service and other policies</li>
                <li>Prevent fraud, abuse, and illegal activity</li>
                <li>Protect the rights, property, and safety of our users and third parties</li>
                <li>Comply with legal obligations and respond to legal requests</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">4.5 For Legal and Compliance Purposes</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Comply with applicable laws, regulations, and legal processes</li>
                <li>Respond to lawful requests from public authorities</li>
                <li>Establish, exercise, or defend legal claims</li>
                <li>Enforce our agreements and policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Third-Party Services and Data Sharing</h2>
              <p className="mb-4">We use third-party services to help us operate and improve the App. These services may collect, process, and store your information on our behalf.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Third-Party Service Providers</h3>

              <p className="mb-4"><strong>Database and Authentication Service Providers</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Database management, user authentication, data storage, and backend functions</li>
                <li>Data Shared: Personal information, account data, user-generated content, authentication credentials</li>
              </ul>

              <p className="mb-4"><strong>Payment and Subscription Management Services</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Subscription and in-app purchase management</li>
                <li>Data Shared: Transaction data, subscription status, device identifiers</li>
              </ul>

              <p className="mb-4"><strong>Apple Inc.</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Social login (Sign in with Apple), payment processing, App Store services</li>
                <li>Data Shared: Authentication data, payment information, device information</li>
                <li>Privacy Policy: <a href="https://www.apple.com/legal/privacy/" className="text-green-600 hover:text-green-700">https://www.apple.com/legal/privacy/</a></li>
              </ul>

              <p className="mb-4"><strong>Google LLC</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Social login (Google Sign-In), payment processing, Google Play services</li>
                <li>Data Shared: Authentication data, payment information, device information</li>
                <li>Privacy Policy: <a href="https://policies.google.com/privacy" className="text-green-600 hover:text-green-700">https://policies.google.com/privacy</a></li>
              </ul>

              <p className="mb-4"><strong>Error Monitoring and Analytics Services</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Error tracking, crash monitoring, and performance monitoring</li>
                <li>Data Shared: Error logs, crash reports, device information, usage data</li>
              </ul>

              <p className="mb-4"><strong>Mapping and Location Service Providers</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Location services, mapping, and golf course data</li>
                <li>Data Shared: Location data, search queries, map interactions</li>
              </ul>

              <p className="mb-4"><strong>Push Notification Service Providers</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Purpose: Push notifications</li>
                <li>Data Shared: Push notification tokens, device information, notification interaction data</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 Data Sharing Practices</h3>
              <p className="mb-4">We may share your information in the following circumstances:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>With Service Providers:</strong> We share information with third-party vendors and service providers who perform services on our behalf, such as hosting, data analysis, payment processing, customer service, and marketing.</li>
                <li><strong>For Business Transfers:</strong> If we are involved in a merger, acquisition, financing, reorganization, bankruptcy, or sale of assets, your information may be transferred as part of that transaction.</li>
                <li><strong>With Your Consent:</strong> We may share your information with third parties when you have given us explicit consent to do so.</li>
                <li><strong>For Legal Purposes:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, government agencies).</li>
                <li><strong>To Protect Rights and Safety:</strong> We may share information to protect and defend our rights, property, or safety, or that of our users or others, including enforcing our Terms of Service.</li>
                <li><strong>Aggregated or Anonymized Data:</strong> We may share aggregated or de-identified information that cannot reasonably be used to identify you.</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">5.3 We Do Not Sell Your Personal Information</h3>
              <p>We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="mb-4">We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.1 Retention Periods</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Retained for the duration of your active account and for a reasonable period thereafter to comply with legal obligations</li>
                <li><strong>Transaction Records:</strong> Retained for at least 7 years for tax and accounting purposes</li>
                <li><strong>User-Generated Content:</strong> Retained until you delete the content or close your account</li>
                <li><strong>Usage Logs:</strong> Typically retained for 12-24 months for analytics and security purposes</li>
                <li><strong>Communication Records:</strong> Retained for 3-5 years for customer service and legal purposes</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">6.2 Account Deletion</h3>
              <p>When you request account deletion, we will delete or anonymize your personal information within 30 days, except where we are required to retain certain information for legal, regulatory, security, or legitimate business purposes.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="mb-4">Depending on your jurisdiction, you may have certain rights regarding your personal information:</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.1 Access and Portability</h3>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request a copy of your personal information in a structured, commonly used, and machine-readable format</li>
                <li>Transmit your data to another service provider (data portability)</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.2 Correction and Update</h3>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Correct inaccurate or incomplete personal information</li>
                <li>Update your account information and preferences at any time through the App settings</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.3 Deletion</h3>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Request deletion of your personal information</li>
                <li>Close your account and have your data removed from our systems</li>
              </ul>
              <p className="mb-4">Please note that we may retain certain information as required by law or for legitimate business purposes.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.4 Restriction and Objection</h3>
              <p className="mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Restrict or object to the processing of your personal information in certain circumstances</li>
                <li>Opt out of marketing communications</li>
                <li>Disable location services and push notifications</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.5 Withdraw Consent</h3>
              <p className="mb-4">Where we rely on your consent to process your personal information, you have the right to withdraw that consent at any time.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">7.6 Exercising Your Rights</h3>
              <p>
                To exercise any of these rights, please contact us at <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a>. We will respond to your request within 30 days. You may be required to verify your identity before we can fulfill your request.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Security Measures</h2>
              <p className="mb-4">We implement reasonable administrative, technical, and physical security measures to protect your personal information from unauthorized access, use, disclosure, alteration, and destruction.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.1 Security Practices</h3>
              <p className="mb-4">Our security measures include:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Encryption of data in transit using SSL/TLS protocols</li>
                <li>Encryption of sensitive data at rest</li>
                <li>Secure authentication mechanisms and password hashing</li>
                <li>Regular security assessments and vulnerability testing</li>
                <li>Access controls and authentication requirements for our personnel</li>
                <li>Monitoring and logging of system access</li>
                <li>Regular backups and disaster recovery procedures</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.2 Third-Party Security</h3>
              <p className="mb-4">We carefully select third-party service providers and require them to implement appropriate security measures to protect your information.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">8.3 Limitations</h3>
              <p>While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your information. You are responsible for maintaining the confidentiality of your account credentials and for any activity that occurs under your account.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Age Restrictions and COPPA Compliance</h2>
              <p className="mb-4">The Hole In 1 app is intended for users who are at least 18 years of age. We do not knowingly collect personal information from anyone under the age of 18.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.1 Children's Privacy</h3>
              <p className="mb-4">We comply with the Children's Online Privacy Protection Act (COPPA). We do not knowingly:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Collect, use, or disclose personal information from children under 13</li>
                <li>Allow children under 13 to create accounts or use the Services</li>
                <li>Target our Services to children under 13</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.2 Age Verification</h3>
              <p className="mb-4">By using the Services, you represent that you are at least 18 years of age. We may implement age verification mechanisms to ensure compliance with this requirement.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">9.3 Parental Notice</h3>
              <p>
                If we become aware that we have collected personal information from a child under 18 without verification of parental consent, we will take steps to delete that information as quickly as possible. If you believe we might have information from or about a child under 18, please contact us at <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Privacy Laws</h2>
              <p className="mb-4">We are committed to compliance with applicable privacy laws and regulations worldwide.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.1 GDPR Compliance (European Economic Area)</h3>
              <p className="mb-4">If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland, you have additional rights under the General Data Protection Regulation (GDPR):</p>

              <p className="mb-4"><strong>Legal Basis for Processing</strong></p>
              <p className="mb-4">We process your personal information based on the following legal grounds:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Consent:</strong> When you have given explicit consent for specific processing activities</li>
                <li><strong>Contract Performance:</strong> To fulfill our contractual obligations to provide the Services</li>
                <li><strong>Legitimate Interests:</strong> For our legitimate business interests, provided they do not override your rights</li>
                <li><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</li>
              </ul>

              <p className="mb-4"><strong>Data Transfers</strong></p>
              <p className="mb-4">Your information may be transferred to and processed in the United States and other countries where our service providers operate. We ensure that such transfers are protected by appropriate safeguards, such as Standard Contractual Clauses approved by the European Commission.</p>

              <p className="mb-4"><strong>Supervisory Authority</strong></p>
              <p className="mb-4">You have the right to lodge a complaint with your local data protection authority if you believe we have violated your privacy rights.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.2 CCPA Compliance (California)</h3>
              <p className="mb-4">If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA):</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Know:</strong> You have the right to know what personal information we collect, use, disclose, and sell (though we do not sell personal information).</li>
                <li><strong>Right to Delete:</strong> You have the right to request deletion of your personal information, subject to certain exceptions.</li>
                <li><strong>Right to Opt-Out:</strong> You have the right to opt out of the sale of personal information (we do not sell personal information).</li>
                <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
              </ul>

              <p className="mb-4"><strong>Categories of Information Collected (CCPA):</strong></p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Identifiers (name, email, username, device ID, IP address)</li>
                <li>Commercial information (purchase history, subscription status)</li>
                <li>Internet or network activity (usage data, log files)</li>
                <li>Geolocation data (GPS coordinates)</li>
                <li>Audio, electronic, visual, or similar information (videos, photos)</li>
                <li>Inferences drawn from personal information (preferences, behavior)</li>
              </ul>

              <p className="mb-4"><strong>California Privacy Rights Requests</strong></p>
              <p className="mb-4">
                To submit a verifiable consumer request under CCPA, please contact us at <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a>. We will verify your identity and respond within 45 days.
              </p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">10.3 Other Jurisdictions</h3>
              <p>We strive to comply with privacy laws in all jurisdictions where we operate. If you have questions about how your local privacy laws apply to our Services, please contact us.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Tracking Technologies</h2>
              <p className="mb-4">We use mobile tracking technologies to collect and store information about your use of the Services.</p>

              <h3 className="text-xl font-medium text-gray-900 mb-3">11.1 Types of Technologies</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Local Storage:</strong> We use on-device storage (such as AsyncStorage) to maintain your session, cache preferences, and store temporary data locally on your device.</li>
                <li><strong>SDKs and APIs:</strong> Third-party software development kits integrated into the App that collect usage, error, and performance data.</li>
                <li><strong>Log Files:</strong> Automatically collected information about your device and App usage stored in log files.</li>
                <li><strong>Sponsor Exposure Tracking:</strong> We track which sponsor content you are shown within the App, including the context, duration, and type of exposure. This data is associated with your user account and sent to our servers to measure sponsorship performance.</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">11.2 Purposes of Tracking</h3>
              <p className="mb-4">We use these technologies to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Authenticate users and maintain sessions</li>
                <li>Remember user preferences and settings</li>
                <li>Monitor errors and crashes</li>
                <li>Deliver push notifications</li>
                <li>Measure sponsor content exposure</li>
                <li>Improve user experience</li>
              </ul>

              <h3 className="text-xl font-medium text-gray-900 mb-3">11.3 Managing Tracking Preferences</h3>
              <p className="mb-4">You can control some tracking technologies through your device settings:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Push Notifications:</strong> Disable push notifications in device settings</li>
                <li><strong>Location Services:</strong> Enable or disable location permissions in device settings</li>
                <li><strong>App Data:</strong> Clear app data and cache through device settings</li>
              </ul>
              <p>Please note that disabling certain features may affect the functionality of the Services.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Do Not Track Signals</h2>
              <p>Some web browsers and devices have a "Do Not Track" (DNT) feature that signals websites and apps not to track users. Currently, there is no uniform standard for recognizing and responding to DNT signals. As such, we do not currently respond to DNT signals.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Third-Party Links</h2>
              <p>The App may contain links to third-party websites, services, or resources that are not owned or controlled by us. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services you access through our App.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to This Privacy Policy</h2>
              <p className="mb-4">We reserve the right to modify this Privacy Policy at any time. When we make changes, we will:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Notify you through the App or via email (if you have provided an email address)</li>
                <li>In case of material changes, request your consent where required by law</li>
              </ul>
              <p>Your continued use of the Services after any changes to this Privacy Policy constitutes your acceptance of the revised policy. We encourage you to review this Privacy Policy periodically.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. International Data Transfers</h2>
              <p className="mb-4">Kia Kaha Labs LLC is based in the United States. Your information may be transferred to, stored, and processed in the United States and other countries where our service providers operate. These countries may have data protection laws that differ from those of your country of residence.</p>
              <p>By using the Services, you consent to the transfer of your information to the United States and other countries. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Your California Privacy Rights (Shine the Light Law)</h2>
              <p>California Civil Code Section 1798.83 permits California residents to request certain information about the disclosure of personal information to third parties for direct marketing purposes. We do not share personal information with third parties for their direct marketing purposes without your consent.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
              <p className="mb-4">If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:</p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="mb-2"><strong>Kia Kaha Labs LLC</strong></p>
                <p className="mb-2">Email: <a href="mailto:info@holein1app.com" className="text-green-600 hover:text-green-700">info@holein1app.com</a></p>
                <p className="mb-4">Subject Line: Privacy Policy Inquiry</p>
                <p className="mb-2">For GDPR-related inquiries or to exercise your rights under GDPR, please include "GDPR Request" in the subject line.</p>
                <p className="mb-2">For CCPA-related inquiries or to exercise your rights under CCPA, please include "CCPA Request" in the subject line.</p>
                <p>We will make every effort to respond to your inquiry within 30 days (or as required by applicable law).</p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">18. Consent</h2>
              <p>By using the Hole In 1 app and Services, you consent to the collection, use, and sharing of your information as described in this Privacy Policy. If you do not agree with this Privacy Policy, please do not use the Services.</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8" style={{ backgroundColor: '#F0F5E8' }}>
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>© {new Date().getFullYear()} Hole In 1 App. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <a href="/privacy" className="text-green-600 hover:text-green-700">Privacy Policy</a>
            <a href="/terms" className="text-green-600 hover:text-green-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
